'use client';

import { useTranslations } from 'next-intl';
import { toast } from '@/app/components/ui/use-toast';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import {
  CommunityEnum,
  type UpdateInteractionStatusParams,
  type UpdateLikeStatusParamsWithCreation,
} from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { votesUnvote, votesVote } from '../apis';

export interface UseCommunityInteractionConfig {
  onInteractionStatusChange?: (params: UpdateInteractionStatusParams | UpdateLikeStatusParamsWithCreation) => void;
}

/**
 * 统一的社区交互 Hook
 * 支持：
 * 点赞/取消点赞
 * 收藏/取消收藏
 */
export const useCommunityInteraction = ({ onInteractionStatusChange }: UseCommunityInteractionConfig) => {
  const trans = useTranslations();
  const pathname = usePathname();
  // 点赞
  const { run: vote, loading: voting } = useFetch(
    ({ id: obj_id, obj_type, voteType }: UpdateLikeStatusParamsWithCreation) =>
      votesVote({
        path: {
          obj_id,
          obj_type,
        },
        body: {
          vote: voteType ?? 'like',
          obj_type,
          obj_id,
        },
      }),
    {
      manual: true,
      onBefore: (params: [UpdateLikeStatusParamsWithCreation]) => {
        const { traceId, obj_type, id } = params[0];
        if (obj_type !== 'creation') {
          reportEvent(TRACE_EVENT_NAME[obj_type], {
            [TRACE_EVENT_ID[obj_type]]: id,
            interaction_type: 'like',
            interaction_position: pathname,
            trace_id: traceId || '',
            content_type: obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
          });
        }
      },
      onSuccess: ({ error }, params: [UpdateLikeStatusParamsWithCreation]) => {
        if (error) {
          onInteractionStatusChange?.({
            id: params[0].id,
            traceId: params[0].traceId,
            obj_type: params[0].obj_type,
            voted: false,
            voteType: params[0].voteType,
          });
          toast({
            type: 'error',
            message: trans('popup.toast.operationFailed'),
          });
          return;
        }
        onInteractionStatusChange?.({
          id: params[0].id,
          traceId: params[0].traceId,
          obj_type: params[0].obj_type,
          voted: true,
          voteType: params[0].voteType,
        });
      },
    },
  );

  // 取消点赞
  const { run: unVote, loading: unvoting } = useFetch(
    ({ id: obj_id, obj_type, voteType }: UpdateLikeStatusParamsWithCreation) =>
      votesUnvote({
        path: {
          obj_id,
          obj_type,
        },
        query: {
          vote: voteType ?? 'like',
        },
      }),
    {
      manual: true,
      onBefore: (params: [UpdateLikeStatusParamsWithCreation]) => {
        const { traceId, obj_type, id } = params[0];
        if (obj_type !== 'creation') {
          reportEvent(TRACE_EVENT_NAME[obj_type], {
            [TRACE_EVENT_ID[obj_type]]: id,
            interaction_type: `cancel_${params[0].voteType ?? 'like'}`,
            interaction_position: pathname,
            trace_id: traceId || '',
            content_type: obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
          });
        }
      },
      onSuccess: ({ error }, params: [UpdateLikeStatusParamsWithCreation]) => {
        if (error) {
          onInteractionStatusChange?.({
            id: params[0].id,
            traceId: params[0].traceId,
            obj_type: params[0].obj_type,
            voted: true,
            voteType: params[0].voteType,
          });
          toast({
            type: 'error',
            message: trans('popup.toast.operationFailed'),
          });
          return;
        }
        onInteractionStatusChange?.({
          id: params[0].id,
          traceId: params[0].traceId,
          obj_type: params[0].obj_type,
          voted: false,
          voteType: params[0].voteType,
        });
      },
    },
  );

  // 收藏
  const { run: collect, loading: collecting } = useFetch(
    ({ id: obj_id, obj_type }: UpdateInteractionStatusParams) =>
      votesVote({
        path: {
          obj_id,
          obj_type,
        },
        body: {
          vote: 'collect',
          obj_type,
          obj_id,
        },
      }),
    {
      manual: true,
      onBefore: (params: [UpdateInteractionStatusParams]) => {
        const { traceId, obj_type, id } = params[0];
        reportEvent(TRACE_EVENT_NAME[obj_type], {
          [TRACE_EVENT_ID[obj_type]]: id,
          interaction_type: 'star',
          interaction_position: pathname,
          trace_id: traceId || '',
          content_type: obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
        });
      },
      onSuccess: ({ error }, params: [UpdateInteractionStatusParams]) => {
        if (error) {
          onInteractionStatusChange?.({
            id: params[0].id,
            traceId: params[0].traceId,
            obj_type: params[0].obj_type,
            collected: false,
            collectType: params[0].collectType,
          });
          toast({
            type: 'error',
            message: trans('popup.toast.operationFailed'),
          });
          return;
        }
        toast({
          type: 'success',
          message: trans('task.action.collectSuccess'),
        });
        onInteractionStatusChange?.({
          id: params[0].id,
          traceId: params[0].traceId,
          obj_type: params[0].obj_type,
          collected: true,
          collectType: params[0].collectType,
        });
      },
    },
  );

  // 取消收藏
  const { run: unCollect, loading: uncollecting } = useFetch(
    ({ id: obj_id, obj_type }: UpdateInteractionStatusParams) =>
      votesUnvote({
        path: {
          obj_id,
          obj_type,
        },
        query: {
          vote: 'collect',
        },
      }),
    {
      manual: true,
      onBefore: (params: [UpdateInteractionStatusParams]) => {
        const { traceId, obj_type, id } = params[0];
        reportEvent(TRACE_EVENT_NAME[obj_type], {
          [TRACE_EVENT_ID[obj_type]]: id,
          interaction_type: 'cancel_collect',
          interaction_position: pathname,
          trace_id: traceId || '',
          content_type: obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
        });
      },
      onSuccess: ({ error }, params: [UpdateInteractionStatusParams]) => {
        if (error) {
          onInteractionStatusChange?.({
            id: params[0].id,
            traceId: params[0].traceId,
            obj_type: params[0].obj_type,
            collected: true,
            collectType: params[0].collectType,
          });
          toast({
            type: 'error',
            message: trans('popup.toast.operationFailed'),
          });
          return;
        }
        onInteractionStatusChange?.({
          id: params[0].id,
          traceId: params[0].traceId,
          obj_type: params[0].obj_type,
          collected: false,
          collectType: params[0].collectType,
        });
      },
    },
  );

  return {
    loading: voting || unvoting || collecting || uncollecting,
    vote,
    unVote,
    collect,
    unCollect,
  };
};
